import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { ExternalLink } from 'lucide-react'
import { useAuth } from '@contexts/AuthContext'
import { useSystem } from '@contexts/SystemContext'
import { HUB_SECURITY_SETTINGS_URL } from '@/constants/hub'
import type { LoginCredentials, LoginOutcome } from '@/types'
import MfaVerifyForm from './MfaVerifyForm'

const LoginPage: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false)
  // 兩步驟驗證第二步的憑證：只放在記憶體（不寫進網址或 storage），有值就顯示輸入驗證碼的畫面
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  // 回到第一步時要說明的原因（例如驗證時間已過）
  const [notice, setNotice] = useState<string | null>(null)
  // 帳號被要求開兩步驟驗證但還沒設定：顯示訊息與到會員中樞設定的連結
  const [setupMessage, setSetupMessage] = useState<string | null>(null)
  const { login, googleLogin, verifyMfa, user, isLoading } = useAuth()
  const { systemSettings } = useSystem()
  const navigate = useNavigate()

  const softwareName = systemSettings?.softwareName || 'AnyTime'

  useEffect(() => {
    if (user && !isLoading) {
      navigate('/')
    }
  }, [user, isLoading, navigate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const resetMessages = () => {
    setNotice(null)
    setSetupMessage(null)
  }

  const handleLoginOutcome = (outcome: LoginOutcome) => {
    if (outcome.status === 'mfa_required') {
      // 第二步用不到密碼，不留在記憶體裡
      setCredentials(prev => ({ ...prev, password: '' }))
      setMfaToken(outcome.mfaToken)
    } else if (outcome.status === 'mfa_setup_required') {
      setSetupMessage(outcome.message)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!credentials.email || !credentials.password) {
      return
    }

    resetMessages()
    setIsSubmitting(true)
    try {
      handleLoginOutcome(await login(credentials))
    } catch (error: any) {
      console.error('Login failed:', error?.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) return
    resetMessages()
    setIsGoogleSubmitting(true)
    try {
      handleLoginOutcome(await googleLogin(credentialResponse.credential))
    } catch (error: any) {
      console.error('Google login failed:', error?.message)
    } finally {
      setIsGoogleSubmitting(false)
    }
  }

  const handleGoogleError = () => {
    console.error('Google login error')
  }

  const backToCredentials = (message: string | null = null) => {
    setMfaToken(null)
    setNotice(message)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-secondary-600 font-medium">載入中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="max-w-md w-full mx-4">
        <div className="card-premium p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gradient mb-2">{softwareName}</h1>
            <p className="text-secondary-600">線上計時工具</p>
          </div>

          {mfaToken ? (
            <MfaVerifyForm
              onVerify={(code) => verifyMfa(mfaToken, code)}
              onCancel={() => backToCredentials()}
              onRestart={(message) => backToCredentials(message)}
              onSetupRequired={(message) => {
                setMfaToken(null)
                setSetupMessage(message)
              }}
            />
          ) : (
            <>
              {notice && (
                <div role="status" className="mb-6 rounded-xl border border-primary-200 bg-primary-50 p-4 text-sm text-secondary-700">
                  {notice}
                </div>
              )}

              {setupMessage && (
                <div role="alert" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  <p>{setupMessage}</p>
                  <a
                    href={HUB_SECURITY_SETTINGS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 font-medium text-amber-900 underline underline-offset-2"
                  >
                    到會員中樞設定
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    電子郵件
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={credentials.email}
                    onChange={handleInputChange}
                    className="input-primary"
                    placeholder="請輸入您的電子郵件"
                    required
                    disabled={isSubmitting || isGoogleSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    密碼
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={credentials.password}
                    onChange={handleInputChange}
                    className="input-primary"
                    placeholder="請輸入您的密碼"
                    required
                    disabled={isSubmitting || isGoogleSubmitting}
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={isSubmitting || isGoogleSubmitting || !credentials.email || !credentials.password}
                >
                  {isSubmitting ? '登入中...' : '登入'}
                </button>
              </form>

              {/* 分隔線 */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-primary-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-secondary-500">或使用以下方式登入</span>
                </div>
              </div>

              {/* Google 登入按鈕 */}
              <div className={`flex justify-center ${isGoogleSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  text="signin_with"
                />
              </div>
            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-secondary-600">
              你的時間正在創造有意義的價值
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
